import { useRef, useState } from 'react';
import type { Theme } from '@sokdaq/protocol';
import { useDismiss } from '../hooks/useDismiss.js';
import { useIsTouch } from '../hooks/useTouch.js';
import { isDesktop } from '../platform/bridge.js';
import { useChatStore } from '../store/chat.js';
import { useUiStore } from '../store/ui.js';
import { DesktopControls } from './DesktopControls.js';
import { SettingsPanel } from './SettingsPanel.js';

const VIEW_LABELS: Record<Theme, string> = {
  office: '오피스',
  stealth: '스텔스',
  classic: '클래식',
};

export function RoomHeader({ onLeave }: { onLeave: () => void }) {
  const view = useUiStore((s) => s.view);
  const setView = useUiStore((s) => s.setView);
  const miniMode = useUiStore((s) => s.miniMode);
  const bossKeyGlobal = useUiStore((s) => s.bossKeyGlobal);
  const room = useChatStore((s) => s.room);
  // 코드는 스토어에서 읽는다. 방장이 회전시키면 여기도 따라 바뀌어야 한다 —
  // 화면 밖에서 들고 다니면 낡은 코드를 복사해 남에게 전달하게 된다.
  const code = useChatStore((s) => s.code) ?? '…';
  const members = useChatStore((s) => s.members);
  const isTouch = useIsTouch();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const onlineCount = members.filter((m) => m.online).length;

  /*
   * 좁은 화면에서 접는 것들: 방 이름, 초대코드, 나가기.
   *
   * 뷰 전환과 설정은 접지 않는다. 뷰 전환은 이 앱의 본체이고, 모바일에서
   * 위장 화면으로 넘어가는 유일한 통로다 — 보스키가 없으니까.
   */
  return (
    <header className="relative flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-neutral-800 px-2 py-1.5 sm:gap-x-3 sm:px-3 sm:py-2">
      <span className="shrink-0 font-semibold tracking-tight">속닥</span>

      {/* 미니 모드에서는 폭이 320px다. 방 이름과 코드까지 넣으면 줄이 넘친다. */}
      {!miniMode && (
        <span className="hidden items-center gap-1 truncate text-sm text-neutral-300 sm:inline-flex">
          {room?.name ?? '…'}
          {room?.locked === true && <LockedBadge />}
        </span>
      )}
      {!miniMode && (
        <span className="hidden sm:inline">
          <InviteCode code={code} />
        </span>
      )}

      <span className="shrink-0 text-xs text-neutral-500">
        <span className="text-emerald-400">●</span> {onlineCount}
        <span className="hidden sm:inline">명</span>
      </span>

      <nav className="ml-auto flex shrink-0 gap-0.5 sm:gap-1" aria-label="뷰 전환">
        {(Object.keys(VIEW_LABELS) as Theme[]).map((candidate) => {
          // 좁은 창에서 오피스 맵은 다섯 타일밖에 안 보인다.
          const unavailable = miniMode && candidate === 'office';
          return (
            <button
              key={candidate}
              type="button"
              onClick={() => setView(candidate)}
              disabled={unavailable}
              aria-current={view === candidate}
              title={unavailable ? '미니 모드에서는 쓸 수 없습니다' : undefined}
              className={`tap rounded px-1.5 text-xs sm:px-2 sm:py-1 sm:text-sm ${
                view === candidate
                  ? 'bg-neutral-100 text-neutral-900'
                  : unavailable
                    ? 'text-neutral-700'
                    : 'text-neutral-400 hover:bg-neutral-800'
              }`}
            >
              {VIEW_LABELS[candidate]}
            </button>
          );
        })}
      </nav>

      {/*
        폭으로 감추지 않는다. 미니 모드는 320px짜리 데스크톱 창인데,
        여기서 감추면 미니 모드를 풀 통로가 트레이 메뉴밖에 남지 않는다.
        데스크톱에서만 렌더되므로 모바일에는 애초에 나타나지 않는다.
      */}
      <DesktopControls />

      <button
        type="button"
        onClick={() => setSettingsOpen((open) => !open)}
        data-settings-toggle
        aria-expanded={settingsOpen}
        aria-label="설정"
        title="설정"
        className={`tap shrink-0 rounded px-2 text-sm sm:py-1 ${
          settingsOpen
            ? 'bg-neutral-800 text-neutral-200'
            : 'text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300'
        }`}
      >
        ⚙
      </button>

      <button
        type="button"
        onClick={onLeave}
        className="hidden rounded px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300 sm:block"
      >
        나가기
      </button>

      {/* 좁은 화면에서 접어 둔 것들의 입구. */}
      <div className="shrink-0 sm:hidden">
        <OverflowMenu
          open={overflowOpen}
          onToggle={() => setOverflowOpen((open) => !open)}
          onClose={() => setOverflowOpen(false)}
          code={code}
          roomName={room?.name ?? '…'}
          locked={room?.locked === true}
          onLeave={onLeave}
        />
      </div>

      {!miniMode && !isTouch && (
        <span className="hidden text-[10px] text-neutral-600 sm:block sm:w-auto">
          {isDesktop ? '데스크톱' : '웹'} · 보스키 Ctrl+Shift+X
          {/*
            전역 등록이 실패하면 창에 포커스가 있을 때만 동작한다.
            조용히 반쪽만 되는 것보다 알려주는 게 낫다.
          */}
          {isDesktop && !bossKeyGlobal && ' (이 창에서만)'}
        </span>
      )}

      {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}
    </header>
  );
}

/**
 * 좁은 화면용 넘침 메뉴.
 *
 * 방 이름·초대코드·나가기가 들어간다. 자주 쓰지 않지만 없으면 곤란한
 * 것들이다 — 특히 초대코드는 사람을 부르는 유일한 수단이다.
 */
function OverflowMenu({
  open,
  onToggle,
  onClose,
  code,
  roomName,
  locked,
  onLeave,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  code: string;
  roomName: string;
  locked: boolean;
  onLeave: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useDismiss(menuRef, open, onClose, { toggleSelector: '[data-overflow-toggle]' });

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        data-overflow-toggle
        aria-expanded={open}
        aria-label="더 보기"
        className={`tap rounded px-2 text-sm ${
          open ? 'bg-neutral-800 text-neutral-200' : 'text-neutral-500'
        }`}
      >
        ⋯
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="더 보기"
          className="absolute right-2 top-full z-20 mt-1 w-56 rounded-lg border border-neutral-700 bg-neutral-900 p-2 shadow-xl"
        >
          <p className="flex items-center gap-1 truncate px-1 pb-1 text-sm text-neutral-300">
            {roomName}
            {locked && <LockedBadge />}
          </p>
          <div className="px-1 pb-2">
            <InviteCode code={code} />
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onClose();
              onLeave();
            }}
            className="tap w-full rounded px-1 text-left text-sm text-neutral-400 hover:bg-neutral-800"
          >
            나가기
          </button>
        </div>
      )}
    </>
  );
}

/** 잠긴 방 표시. 새로 들어올 수 없다는 뜻이라 눈에 띄어야 한다. */
function LockedBadge() {
  return (
    <span title="잠김 — 새로 들어올 수 없습니다" className="text-xs text-amber-400">
      잠김
    </span>
  );
}

/**
 * 초대코드. 누르면 복사된다.
 *
 * 사람을 부르려면 이걸 전달해야 하는데, 매번 손으로 옮겨 적게 하면
 * 오타가 난다. 그래서 코드 정규화에 오타 관용 규칙도 넣어 두었다.
 */
function InviteCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 클립보드 권한이 없으면 조용히 넘어간다. 코드는 화면에 보인다.
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      title="클릭하면 복사됩니다"
      className="tap rounded bg-neutral-900 px-2 py-0.5 font-mono text-xs text-neutral-400 hover:text-neutral-200"
    >
      {copied ? '복사됨!' : code}
    </button>
  );
}
